import ConfirmModal from '@creator/ui/components/ConfirmModal/ConfirmModal';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { getUserId } from '@src/model/user/helpers';
import { type Flair, attachFlair } from '@src/store/flairs/flairs';
import { usePost } from '@src/store/posts/hooks/usePost';
import { useContext, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import FlairsCard from '../FlairsCard/FlairsCard';

export interface EditFlairModalProps {
    className?: string;
    tokenName: string;
    postId: string;
}

const EditFlairModal: FC<EditFlairModalProps> = props => {
    const { postId, tokenName } = props;

    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFlair, setSelectedFlair] = useState('');

    const post = usePost(postId);
    const initialFlair = post?.flairIds ? post.flairIds[0] : '';
    const isFlairMandatory = post?.kind === 'story';
    const didFlairChange = selectedFlair && initialFlair !== selectedFlair;

    const { handleModal } = useContext<UseModal>(ModalContext);

    async function editFlair(): Promise<void> {
        if (!post) return;

        setIsLoading(true);
        await attachFlair({
            flairId: selectedFlair,
            postId,
            tokenName,
            userId: getUserId(),
        });
        setIsLoading(false);
        handleModal(null);
    }

    function onFlairClick(flairId: Flair['id']) {
        setSelectedFlair(flairId);
    }

    function renderFlairSelection() {
        return (
            <div className="pt-8">
                <FlairsCard isFlairMandatory={isFlairMandatory} tokenName={tokenName} selectedFlair={selectedFlair || initialFlair} onFlairClick={onFlairClick} />
            </div>
        );
    }

    return (
        <ConfirmModal
            title={t('editFlair.title')}
            subtitle={t('editFlair.editFlairSubtitle')}
            onClick={() => handleModal(null)}
            okButtonProps={{
                isDisabled: !didFlairChange,
                onClick: () => editFlair(),
                isLoading,
            }}
        >
            {renderFlairSelection()}
        </ConfirmModal>
    );
};

export default EditFlairModal;
